import React, { useState, useEffect } from 'react';
import { ThinkingState } from '../types';
import { cleanRedactionBlocks } from '../utils/text';

interface ReasoningProcessProps {
    thinking: string;
    thinkingState: ThinkingState;
    onToggleThinking: () => void;
    thinkingTime?: number; // Time spent thinking in seconds
    isLive?: boolean; // Flag to indicate active thinking in progress
}

const ReasoningProcess: React.FC<ReasoningProcessProps> = ({
    thinking,
    thinkingState,
    onToggleThinking,
    thinkingTime = 0,
    isLive = false
}) => {
    const [displayTime, setDisplayTime] = useState('0.0s');
    const [elapsed, setElapsed] = useState(0);
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

    // Start a live timer when actively thinking
    useEffect(() => {
        if (isLive) {
            const interval = setInterval(() => {
                setElapsed(prev => {
                    const newElapsed = prev + 0.1;
                    setDisplayTime(`${newElapsed.toFixed(1)}s`);
                    return newElapsed;
                });
            }, 100);

            setTimer(interval);
            return () => clearInterval(interval);
        } else if (timer) {
            clearInterval(timer);
            setTimer(null);
        }
    }, [isLive]);

    // Update the display time in a formatted way for completed thinking
    useEffect(() => {
        if (thinkingTime && !isLive) {
            // Format the time to 1 decimal place for better readability
            setDisplayTime(`${thinkingTime.toFixed(1)}s`);
        }
    }, [thinkingTime, isLive]);

    if (thinkingState === 'none') return null;

    // Clean redaction blocks from thinking content
    const cleanedThinking = cleanRedactionBlocks(thinking);

    // Determine container class based on live state
    const containerClass = isLive ? 'thinking-container active' : 'thinking-container completed';

    return (
        <div className={containerClass}>
            <div className="thinking-toggle" onClick={onToggleThinking}>
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                        {isLive ? (
                            // Use the new shimmer text animation
                            <span className="shimmer-text">Thinking</span>
                        ) : (
                            // Completed state UI
                            <>
                                <div className="w-4 h-4 text-primary mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                    </svg>
                                </div>
                                <span className="text-primary font-medium">Thought for {displayTime}</span>
                            </>
                        )}
                    </div>
                    {/* Chevron for toggling content (only show if not live and has thinking content) */}
                    {!isLive && thinking && (
                        <div className="flex items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 transition-transform ${thinkingState === 'visible' ? 'rotate-180' : ''}`}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </div>
                    )}
                </div>
            </div>

            {/* Collapsible content (only relevant when not live) */}
            {!isLive && (
                <div className={`thinking-content ${thinkingState === 'visible' ? 'visible' : ''}`}>
                    {cleanedThinking ? (
                        <pre className="text-sm whitespace-pre-wrap text-gray-300 font-mono">{cleanedThinking}</pre>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default ReasoningProcess; 